import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function TemplateDocView() {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [altText, setAltText] = useState('');
  const [savingAlt, setSavingAlt] = useState(false);
  const [altSaved, setAltSaved] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const { authFetch } = useAuth();

  const handleOpenIframe = () => {
    const iframeUrl = `${window.location.origin}/iframe/template-docs/${id}`;
    window.open(iframeUrl, 'template_iframe', 'width=800,height=600,resizable=yes,scrollbars=yes');
  };

  const handleExportCsv = async () => {
    try {
      const response = await authFetch(`/api/v1/signed-docs/export/xlsx/${id}`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error('Не удалось выгрузить XLSX');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const filename = template?.name
        ? `${template.name.replace(/[.,\\/#!$%\\^&\\*;:{}=\\-_`~()]/g, '').trim()}-signed.xlsx`
        : 'signed-docs.xlsx';
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert('Ошибка при выгрузке XLSX. Попробуйте позже.');
    }
  };

  const handleDownload = async () => {
    try {
      const response = await authFetch(`/api/v1/file-download/${template.file_id}`);
      if (response.ok) { 
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const filename = template.name.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, '').trim(); 
        a.download = filename+".pdf" || 'document.pdf'; 
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        setError('Failed to download file');
      }
    } catch (error) {
      setError('Error downloading file: ' + error.message);
    }
  };

  const handleCopyIframeCode = () => {
    const iframeCode = `<iframe src="https://prostopodpishi.ru/iframe/template-docs/${id}" style="width: 100%; height: 100vh;"></iframe>`;
    
    navigator.clipboard.writeText(iframeCode).then(() => {
      // You could add a toast notification here if you want
      console.log('Iframe code copied to clipboard');
    }).catch(err => {
      console.error('Failed to copy to clipboard:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = iframeCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    });
  };

  const handleDeleteChildDoc = async (childDocId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот документ?')) {
      return;
    }

    try {
      const response = await authFetch(`/api/v1/template-child-doc/${id}?child_doc_id=${childDocId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        // Refresh the template data to update the child_doc_ids list
        const templateResponse = await authFetch(`/api/v1/template-docs/${id}`);
        if (templateResponse.ok) {
          const updatedTemplate = await templateResponse.json();
          setTemplate(updatedTemplate);
        }
      } else {
        setError('Ошибка при удалении документа');
      }
    } catch (error) {
      setError('Ошибка при удалении документа: ' + error.message);
    }
  };

  const handleDownloadChildDoc = async (childDocId, fileName) => {
    try {
      const response = await authFetch(`/api/v1/file-download/${childDocId}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        
        // Add .pdf extension if not present
        let downloadName = fileName;
        if (!downloadName.includes('.')) {
          downloadName += '.pdf';
        }
        
        a.download = downloadName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        setError('Ошибка при скачивании файла');
      }
    } catch (error) {
      setError('Ошибка при скачивании файла: ' + error.message);
    }
  };



  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const response = await authFetch(`/api/v1/template-docs/${id}`);
        if (response.ok) {
          const data = await response.json();
          setTemplate(data);
          setAltText(data.alt_privacy_text || '');
        } else {
          setError('Failed to fetch template');
        }
      } catch (error) {
        setError('Error fetching template: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplate();
  }, [id, authFetch]);

  const handleSaveAltPrivacyText = async () => {
    setSavingAlt(true);
    setAltSaved(false);
    try {
      const response = await authFetch(`/api/v1/template-docs/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ alt_privacy_text: altText })
      });
      if (response.ok) {
        setTemplate(prev => prev ? { ...prev, alt_privacy_text: altText } : prev);
        setAltSaved(true);
        setTimeout(() => setAltSaved(false), 2000);
      } else {
        setError('Не удалось сохранить альтернативный текст политики');
      }
    } catch (e) {
      setError('Ошибка при сохранении: ' + e.message);
    } finally {
      setSavingAlt(false);
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="container mt-4">
        <div className="alert alert-warning" role="alert">
          Template not found
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
        <h2 className="mb-3 mb-md-0">{template.name}</h2>
        <div className="d-flex flex-wrap gap-2">
          <button 
            className="btn btn-primary"
            onClick={handleDownload}
          >
            <i className="bi bi-download me-2"></i>
            Скачать документ
          </button>
          <button 
            className="btn btn-info"
            onClick={handleOpenIframe}
          >
            <i className="bi bi-window me-2"></i>
            Открыть форму в окне
          </button>
          <button 
            className="btn btn-secondary"
            onClick={handleCopyIframeCode}
          >
            <i className="bi bi-clipboard me-2"></i>
            Скопировать код iframe
          </button>
          <button 
            className="btn btn-success"
            onClick={() => navigate(`/template-docs/${id}/signed`)}
          >
            <i className="bi bi-file-earmark-text me-2"></i>
            Подписанные документы
          </button>
          <button
            className="btn btn-outline-primary"
            onClick={handleExportCsv}
          >
            <i className="bi bi-download me-2"></i>
            Выгрузить EXCEL
          </button>
          <button 
            className="btn btn-outline-secondary"
            onClick={() => navigate('/template-docs')}
          >
            Назад к списку
          </button>
          <button
            className="btn btn-warning"
            onClick={() => navigate(`/template-docs/${id}/update-file`)}
          >
            <i className="bi bi-upload me-2"></i>
            Обновить шаблон
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => navigate(`/template-docs/${id}/upload-child-file`)}
          >
            <i className="bi bi-file-earmark-plus me-2"></i>
            Загрузить доп. документ
          </button>
        </div>
      </div>
      
      <div className="card mb-4">
        <div className="card-body">
          <h5 className="card-title">Информация о шаблоне</h5>
          <div className="row">
            <div className="col-md-6">
              <p><strong>Дата создания:</strong> {new Date(template.created_at).toLocaleString()}</p>
              <p><strong>Дата обновления:</strong> {new Date(template.updated_at).toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-body">
          <h5 className="card-title">Альтернативный текст согласия/политики конфиденциальности</h5>
          <div className="mb-3">
            <label htmlFor="altPrivacyText" className="form-label">Текст (HTML разрешён)</label>
            <textarea
              id="altPrivacyText"
              className="form-control"
              rows="5"
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
              placeholder="Оставьте пустым, чтобы использовать текст по умолчанию"
            />
            <div className="form-text">Если поле пустое, на формах будет использоваться политика по умолчанию.</div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-primary"
              onClick={handleSaveAltPrivacyText}
              disabled={savingAlt}
            >
              {savingAlt ? 'Сохранение...' : 'Сохранить текст'}
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={() => setAltText(template.alt_privacy_text || '')}
              disabled={savingAlt}
            >
              Отменить
            </button>
            {altSaved && (
              <span className="text-success ms-2">Сохранено</span>
            )}
          </div>
        </div>
      </div>

      {template.child_docs && template.child_docs.length > 0 && (
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title">Дочерние документы</h5>
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Название документа</th>
                    <th>UUID документа</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {template.child_docs.map((doc, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>
                        <button
                          className="btn btn-link text-decoration-none p-0"
                          onClick={() => handleDownloadChildDoc(doc.id, doc.readable_name)}
                          title="Скачать документ"
                        >
                          {doc.readable_name}
                        </button>
                      </td>
                      <td>
                        <code className="text-break">{doc.id}</code>
                      </td>
                      <td>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDeleteChildDoc(doc.id)}
                          title="Удалить документ"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <h5 className="card-title">Поля документа</h5>
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Название</th>
                  <th>Тип данных</th>
                  <th>Тег</th>
                  <th>Обязательное</th>
                </tr>
              </thead>
              <tbody>
                {template.sign_doc_fields?.map((field, index) => (
                  <tr key={index}>
                    <td>{field.name}</td>
                    <td>{field.data_type}</td>
                    <td>{field.tag}</td>
                    <td>{field.required ? 
                      <span className="badge bg-warning text-dark">Да</span> : 
                      <span className="badge bg-warning text-dark">Нет</span>
                    }</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TemplateDocView; 